import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
  CardGiftcard as CardGiftcardIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  Palette as PaletteIcon,
  Publish as PublishIcon,
  VisibilityOff as UnpublishedIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { LoadingPage } from '../../../components/ui/LoadingSpinner';
import apiClient from '../../../lib/api';

// Category suggestions for tags
const TAG_SUGGESTIONS = [
  'Religious',
  'Nature',
  'Traditional',
  'Personal',
  'Artistic',
  'Cultural',
  'Uplifting',
  'Simple',
];

const CardDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editForm, setEditForm] = useState({
    title: '',
    status: 'draft',
    tags: [],
    aiReferenceBlocked: false,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteImageDialogOpen, setDeleteImageDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [images, setImages] = useState([]); // Array of { id, imageType }
  const [coverImageId, setCoverImageId] = useState(null);
  const [inside1ImageId, setInside1ImageId] = useState(null);
  const [inside2ImageId, setInside2ImageId] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(new Set()); // Track which images are waiting for thumbnails
  const [thumbnailReady, setThumbnailReady] = useState(new Set()); // Track which thumbnails are ready
  const [imageDimensions, setImageDimensions] = useState({}); // Store dimensions: { imageId: { width, height } }
  const [aspectRatioErrors, setAspectRatioErrors] = useState({}); // Store validation errors: { imageId: errorMessage }
  const [tagInput, setTagInput] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const coverFileInputRef = useRef(null);
  const inside1FileInputRef = useRef(null);
  const inside2FileInputRef = useRef(null);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Helper function to get image URL using new API routes
  const getImageUrl = (cardId, imageId, size = 'full') => {
    const apiBaseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : process.env.REACT_APP_API_URL;
    return `${apiBaseUrl}/api/cards/${cardId}/images/${imageId}/${size}`;
  };

  // Expected aspect ratio for cards: 5×7 inches with 1/8" bleed
  // Bleed: 1/8" on top, bottom, and one side
  // Width: 5 + 1/8 = 5.125 inches
  // Height: 7 + 1/8 + 1/8 = 7.25 inches
  // Aspect ratio: 5.125 / 7.25 = 0.7068965517...
  const EXPECTED_ASPECT_RATIO = 5.125 / 7.25; // Approximately 0.7069
  const ASPECT_RATIO_TOLERANCE_PERCENT = 1; // Allow ±1% tolerance for rounding errors

  // Validate image aspect ratio
  const validateImageAspectRatio = (width, height) => {
    if (!width || !height || width <= 0 || height <= 0) {
      return { valid: false, error: 'Invalid image dimensions' };
    }
    
    const aspectRatio = width / height;
    // Calculate tolerance as a percentage of the expected ratio
    const tolerance = EXPECTED_ASPECT_RATIO * (ASPECT_RATIO_TOLERANCE_PERCENT / 100);
    const minRatio = EXPECTED_ASPECT_RATIO - tolerance;
    const maxRatio = EXPECTED_ASPECT_RATIO + tolerance;
    
    if (aspectRatio < minRatio || aspectRatio > maxRatio) {
      const actualRatio = aspectRatio.toFixed(4);
      const expectedRatio = EXPECTED_ASPECT_RATIO.toFixed(4);
      const percentOff = Math.abs((aspectRatio - EXPECTED_ASPECT_RATIO) / EXPECTED_ASPECT_RATIO * 100).toFixed(2);
      return {
        valid: false,
        error: `Aspect ratio ${actualRatio} (${width}×${height}) does not match required 5.125:7.25 ratio (5×7" card with 1/8" bleed). Off by ${percentOff}%`,
      };
    }
    
    return { valid: true };
  };

  // Validate all images have correct aspect ratio
  const validateAllImages = () => {
    const errors = {};
    const imageIds = [coverImageId, inside1ImageId, inside2ImageId].filter(Boolean);
    
    for (const imageId of imageIds) {
      const dimensions = imageDimensions[imageId];
      if (!dimensions) {
        // Image dimensions not loaded yet - can't validate
        errors[imageId] = 'Image dimensions not available. Please wait for image to load.';
        continue;
      }
      
      const validation = validateImageAspectRatio(dimensions.width, dimensions.height);
      if (!validation.valid) {
        errors[imageId] = validation.error;
      }
    }
    
    setAspectRatioErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle image load to get dimensions
  const handleImageLoad = (imageId, event) => {
    const img = event.target;
    if (img.naturalWidth && img.naturalHeight) {
      setImageDimensions(prev => ({
        ...prev,
        [imageId]: {
          width: img.naturalWidth,
          height: img.naturalHeight,
        },
      }));
      
      // Validate this image
      const validation = validateImageAspectRatio(img.naturalWidth, img.naturalHeight);
      if (!validation.valid) {
        setAspectRatioErrors(prev => ({
          ...prev,
          [imageId]: validation.error,
        }));
      } else {
        // Clear error if validation passes
        setAspectRatioErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[imageId];
          return newErrors;
        });
      }
    }
  };

  // Check if thumbnail is available and ready to display
  // In development, thumbnails aren't generated (no Lambda trigger), so skip this
  const checkThumbnailAvailability = (cardId, imageId) => {
    if (!imageId) return;
    
    // In development, thumbnails don't exist - mark as ready to use full-size
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment) {
      // In development, we'll use full-size images, so mark as "ready" to skip loading state
      setThumbnailReady(prev => new Set(prev).add(imageId));
      return;
    }
    
    const thumbnailUrl = getImageUrl(cardId, imageId, 'thumb');
    const testImg = new Image();
    
    testImg.onload = () => {
      // Thumbnail is available
      setThumbnailReady(prev => new Set(prev).add(imageId));
      setThumbnailLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    };
    
    testImg.onerror = () => {
      // Thumbnail not ready yet, start polling
      setThumbnailLoading(prev => new Set(prev).add(imageId));
      // Retry after delay
      setTimeout(() => {
        checkThumbnailAvailability(cardId, imageId);
      }, 2000); // Check every 2 seconds
    };
    
    testImg.src = thumbnailUrl;
  };

  // Load and validate image dimensions
  const loadAndValidateImage = (cardId, imageId) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setImageDimensions(prev => ({
            ...prev,
            [imageId]: {
              width: img.naturalWidth,
              height: img.naturalHeight,
            },
          }));
          
          // Validate this image
          const validation = validateImageAspectRatio(img.naturalWidth, img.naturalHeight);
          if (!validation.valid) {
            setAspectRatioErrors(prev => ({
              ...prev,
              [imageId]: validation.error,
            }));
          } else {
            // Clear error if validation passes
            setAspectRatioErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[imageId];
              return newErrors;
            });
          }
        }
        resolve();
      };
      img.onerror = () => {
        console.error(`Failed to load image ${imageId} for validation`);
        resolve();
      };
      // Load full-size image to get accurate dimensions
      img.src = getImageUrl(cardId, imageId, 'full');
    });
  };

  // Load images for the card
  const loadImages = async (cardId) => {
    try {
      const response = await apiClient.getCardImages(cardId);
      if (response.success && response.data) {
        // response.data is now an array of { id, imageType }
        setImages(response.data);
        
        // Set image IDs based on type
        const coverImage = response.data.find(img => img.imageType === 'cover');
        const inside1Image = response.data.find(img => img.imageType === 'inside1');
        const inside2Image = response.data.find(img => img.imageType === 'inside2');
        
        setCoverImageId(coverImage?.id || null);
        setInside1ImageId(inside1Image?.id || null);
        setInside2ImageId(inside2Image?.id || null);
        
        // Check thumbnail availability for all images
        response.data.forEach(img => {
          checkThumbnailAvailability(cardId, img.id);
        });

        // Load and validate dimensions for all existing images
        const validationPromises = response.data.map(img => 
          loadAndValidateImage(cardId, img.id)
        );
        await Promise.all(validationPromises);
      }
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };


  useEffect(() => {
    const fetchCard = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const response = await apiClient.getCard(id);
        
        // Backend returns { success: true, data: card }
        if (response.success && response.data) {
          const cardData = response.data;
          setCard(cardData);
          setEditForm({
            title: cardData.title || '',
            status: cardData.status,
            tags: cardData.tags || [],
            aiReferenceBlocked: !!cardData.aiReferenceBlocked,
          });
          // Load images for this card
          await loadImages(id);
        } else {
          showSnackbar('Card not found', 'error');
        }
      } catch (error) {
        console.error('Error fetching card:', error);
        showSnackbar('Failed to load card details', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCard();
  }, [id]);

  // Validate images when they change
  useEffect(() => {
    if (card && (coverImageId || inside1ImageId || inside2ImageId)) {
      // Trigger validation after a short delay to allow images to load
      const timer = setTimeout(() => {
        validateAllImages();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [coverImageId, inside1ImageId, inside2ImageId, imageDimensions, card]);

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddTag = (tagValue) => {
    const tagToAdd = typeof tagValue === 'string' ? tagValue.trim() : (tagInput || '').trim();
    if (tagToAdd && !editForm.tags.includes(tagToAdd)) {
      handleInputChange('tags', [...editForm.tags, tagToAdd]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleInputChange('tags', editForm.tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || !id) {
      return;
    }

    setSaving(true);
    
    try {
      const response = await apiClient.updateCard(id, editForm);
      
      // Backend returns { success: true, data: card }
      if (response.success && response.data) {
        setCard(response.data);
        showSnackbar('Card updated successfully', 'success');
      } else {
        showSnackbar('Failed to update card', 'error');
      }
    } catch (error) {
      console.error('Error updating card:', error);
      showSnackbar('Failed to update card', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!id) return;
    
    const newStatus = editForm.status === 'published' ? 'draft' : 'published';
    
    // If publishing, validate all images have correct aspect ratio
    if (newStatus === 'published') {
      // Check if we have all required images
      const imageIds = [coverImageId, inside1ImageId, inside2ImageId].filter(Boolean);
      if (imageIds.length === 0) {
        showSnackbar('Please upload at least one image before publishing', 'error');
        return;
      }
      
      // Validate all images
      const allValid = validateAllImages();
      if (!allValid) {
        const errorCount = Object.keys(aspectRatioErrors).length;
        const imageTypes = [];
        if (aspectRatioErrors[coverImageId]) imageTypes.push('Cover');
        if (aspectRatioErrors[inside1ImageId]) imageTypes.push('Inside Left');
        if (aspectRatioErrors[inside2ImageId]) imageTypes.push('Inside Right');
        
        showSnackbar(
          `${errorCount} image(s) have incorrect aspect ratio (${imageTypes.join(', ')}). Cards must have a 5:7 aspect ratio.`,
          'error'
        );
        return;
      }
    }
    
    const updatedForm = { ...editForm, status: newStatus };
    
    setSaving(true);
    
    try {
      const response = await apiClient.updateCard(id, { status: newStatus });
      
      // Backend returns { success: true, data: card }
      if (response.success && response.data) {
        setCard(response.data);
        setEditForm(updatedForm);
        showSnackbar(
          newStatus === 'published' ? 'Card added to store successfully' : 'Card removed from store successfully',
          'success'
        );
      } else {
        showSnackbar('Failed to update card status', 'error');
      }
    } catch (error) {
      console.error('Error updating card status:', error);
      showSnackbar('Failed to update card status', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/cards');
  };

  const handleDeleteCard = async () => {
    if (!id) return;
    
    setDeleting(true);
    
    try {
      await apiClient.deleteCard(id);
      showSnackbar('Card deleted successfully', 'success');
      // Redirect back to cards list
      setTimeout(() => {
        navigate('/cards');
      }, 1000);
    } catch (error) {
      console.error('Error deleting card:', error);
      showSnackbar('Failed to delete card', 'error');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const [deletingImageType, setDeletingImageType] = useState(null);
  
  const handleDeleteImage = async (imageType) => {
    if (!id) return;

    // Prevent deletion if card is published
    if (editForm.status === 'published') {
      showSnackbar('Cannot delete images while card is published. Please remove the card from the store first.', 'error');
      setDeleteImageDialogOpen(false);
      return;
    }

    let imageIdToDelete = null;
    if (imageType === 'cover') {
      imageIdToDelete = coverImageId;
    } else if (imageType === 'inside1') {
      imageIdToDelete = inside1ImageId;
    } else if (imageType === 'inside2') {
      imageIdToDelete = inside2ImageId;
    }

    if (!imageIdToDelete) return;
    
    try {
      setDeleting(true);
      await apiClient.deleteCardImage(id, imageIdToDelete);
      
      // Update state to remove the deleted image
      setImages(prev => prev.filter(img => img.id !== imageIdToDelete));
      
      // Clear dimensions and errors for deleted image
      setImageDimensions(prev => {
        const newDims = { ...prev };
        delete newDims[imageIdToDelete];
        return newDims;
      });
      setAspectRatioErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[imageIdToDelete];
        return newErrors;
      });
      
      if (imageType === 'cover') {
        setCoverImageId(null);
      } else if (imageType === 'inside1') {
        setInside1ImageId(null);
      } else if (imageType === 'inside2') {
        setInside2ImageId(null);
      }
      
      showSnackbar('Image deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting image:', error);
      showSnackbar('Failed to delete image', 'error');
    } finally {
      setDeleting(false);
      setDeleteImageDialogOpen(false);
      setDeletingImageType(null);
    }
  };

  const handleImageUpload = async (event, imageType) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    // Prevent upload if card is published
    if (editForm.status === 'published') {
      showSnackbar('Cannot upload images while card is published. Please remove the card from the store first.', 'error');
      // Reset file input
      const fileInputRef = imageType === 'cover' ? coverFileInputRef : 
                          imageType === 'inside1' ? inside1FileInputRef : 
                          inside2FileInputRef;
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }

    // Note: File size validation removed - using S3 pre-signed URLs bypasses API Gateway limits
    // S3 supports files up to 5TB, but we'll keep a reasonable limit for images (100MB)
    if (file.size > 100 * 1024 * 1024) {
      showSnackbar('File size must be less than 100MB', 'error');
      return;
    }

    try {
      await apiClient.uploadCardImage(id, file, imageType);
      
      // Reload images to get the new image ID
      await loadImages(id);
      
      showSnackbar(`${imageType === 'cover' ? 'Cover' : imageType === 'inside1' ? 'Inside Left' : 'Inside Right'} image uploaded successfully`, 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      
      // Extract error message from API response
      let errorMessage = 'Failed to upload image';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showSnackbar(errorMessage, 'error');
    }

    // Reset file input
    const fileInputRef = imageType === 'cover' ? coverFileInputRef : 
                        imageType === 'inside1' ? inside1FileInputRef : 
                        inside2FileInputRef;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = (imageType) => {
    // Prevent upload if card is published
    if (editForm.status === 'published') {
      showSnackbar('Cannot upload images while card is published. Please remove the card from the store first.', 'error');
      return;
    }
    
    const fileInputRef = imageType === 'cover' ? coverFileInputRef : 
                        imageType === 'inside1' ? inside1FileInputRef : 
                        inside2FileInputRef;
    fileInputRef.current?.click();
  };

  // Render a single image upload section
  const renderImageUploadSection = (imageType, label, imageId, fileInputRef) => {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', position: 'relative' }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {label}
          </Typography>
          {imageId && (
            <Tooltip title={editForm.status === 'published' ? 'Remove card from store to delete images' : 'Delete Image'}>
              <span>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    if (editForm.status === 'published') {
                      showSnackbar('Cannot delete images while card is published. Please remove the card from the store first.', 'error');
                      return;
                    }
                    setDeletingImageType(imageType);
                    setDeleteImageDialogOpen(true);
                  }}
                  disabled={editForm.status === 'published'}
                  sx={{ position: 'absolute', right: 0 }}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => handleImageUpload(e, imageType)}
          accept="image/*"
          style={{ display: 'none' }}
        />

        {imageId && card ? (
          <Box sx={{ 
            width: '100%', 
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <Box sx={{ 
              width: '100%', 
              maxWidth: 200,
              minHeight: 200,
              border: '2px solid',
              borderColor: aspectRatioErrors[imageId] ? 'error.main' : 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative',
              boxShadow: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#f5f5f5'
            }}>
              {process.env.NODE_ENV === 'development' || thumbnailReady.has(imageId) ? (
                <img
                  src={getImageUrl(card.id, imageId, process.env.NODE_ENV === 'development' ? 'full' : 'thumb')}
                  alt={`${label} - ${card.title}`}
                  onLoad={(e) => {
                    // Load full-size image to get accurate dimensions
                    const fullSizeImg = new Image();
                    fullSizeImg.onload = (loadEvent) => {
                      handleImageLoad(imageId, loadEvent);
                    };
                    fullSizeImg.src = getImageUrl(card.id, imageId, 'full');
                  }}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    height: 'auto',
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 3
                }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" align="center">
                    Generating thumbnail...
                  </Typography>
                </Box>
              )}
            </Box>
            {aspectRatioErrors[imageId] && (
              <Alert severity="error" sx={{ maxWidth: 200, fontSize: '0.75rem' }}>
                {aspectRatioErrors[imageId]}
              </Alert>
            )}
            {imageDimensions[imageId] && !aspectRatioErrors[imageId] && (
              <Typography variant="caption" color="success.main" sx={{ textAlign: 'center', maxWidth: 200 }}>
                ✓ Valid aspect ratio ({imageDimensions[imageId].width}×{imageDimensions[imageId].height})
              </Typography>
            )}
          </Box>
        ) : (
          <Tooltip title={editForm.status === 'published' ? 'Remove card from store to upload images' : `Click to upload ${label.toLowerCase()} image`} placement="top">
            <Box 
              onClick={() => handleUploadClick(imageType)}
              sx={{ 
                width: '100%', 
                maxWidth: 200,
                height: 200,
                border: '2px dashed',
                borderColor: editForm.status === 'published' ? 'grey.300' : 'primary.main',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: editForm.status === 'published' ? 'grey.50' : 'primary.50',
                cursor: editForm.status === 'published' ? 'not-allowed' : 'pointer',
                opacity: editForm.status === 'published' ? 0.6 : 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: editForm.status === 'published' ? 'grey.50' : 'primary.100',
                  borderColor: editForm.status === 'published' ? 'grey.300' : 'primary.dark',
                  transform: editForm.status === 'published' ? 'none' : 'scale(1.02)'
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                textAlign: 'center',
                p: 2,
                gap: 1
              }}>
                <CloudUploadIcon sx={{ fontSize: 48, color: editForm.status === 'published' ? 'grey.400' : 'primary.main', opacity: 0.6 }} />
                <Typography variant="body2" color={editForm.status === 'published' ? 'text.disabled' : 'primary.main'}>
                  {editForm.status === 'published' ? 'Remove from store to upload' : `No ${label.toLowerCase()} uploaded`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click to upload
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return <LoadingPage message="Loading card details..." />;
  }

  if (!card) {
    return (
      <DashboardLayout title="Card Not Found">
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            Card not found
          </Typography>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Back to Cards
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={`Card: ${card.title}`}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1">
              Edit Card
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Update the card information below
            </Typography>
          </Box>
          {editForm.status !== 'archived' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              {editForm.status !== 'published' && Object.keys(aspectRatioErrors).length > 0 && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Cannot publish: {Object.keys(aspectRatioErrors).length} image(s) have incorrect aspect ratio
                </Alert>
              )}
              <Button
                variant={editForm.status === 'published' ? 'outlined' : 'contained'}
                color={editForm.status === 'published' ? 'warning' : 'success'}
                onClick={handleTogglePublish}
                disabled={saving || (editForm.status !== 'published' && Object.keys(aspectRatioErrors).length > 0)}
                startIcon={editForm.status === 'published' ? <UnpublishedIcon /> : <PublishIcon />}
                sx={{ minWidth: 150 }}
              >
                {editForm.status === 'published' ? 'Remove from Store' : 'Add to Store'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Statistics Cards */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
            gap: 2 
          }}>
            <Card sx={{ 
              background: card.status === 'published' 
                ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
                : card.status === 'draft'
                ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CardGiftcardIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Status
                  </Typography>
                </Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {card.status === 'published' ? 'In Store' : card.status === 'draft' ? 'Not In Store' : card.status}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ShoppingCartIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Sold
                  </Typography>
                </Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  {card.sold || 0}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MoneyIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Revenue
                  </Typography>
                </Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  ${((card.revenue || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 3 
              }}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editForm.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {editForm.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        size="small"
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Autocomplete
                      freeSolo
                      options={TAG_SUGGESTIONS}
                      value={tagInput}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          handleAddTag(newValue);
                        }
                      }}
                      onInputChange={(event, newInputValue) => {
                        setTagInput(newInputValue);
                      }}
                      inputValue={tagInput}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Add a tag"
                          onKeyPress={handleTagInputKeyPress}
                          sx={{ flex: 1 }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => handleAddTag(tagInput)}
                      disabled={!tagInput.trim() || editForm.tags.includes(tagInput.trim())}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!editForm.aiReferenceBlocked}
                      onChange={(e) => handleInputChange('aiReferenceBlocked', e.target.checked)}
                    />
                  }
                  label="Don't allow AI to use this artwork as a reference (&quot;Create one like this&quot;)"
                />

                {/* Action Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                  mt: 4,
                  pt: 3,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 200 },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    {saving ? 'Updating...' : 'Update Card'}
                  </Button>
                </Box>
              </Box>
            </form>
          </CardContent>
        </Card>

        {/* Card Images - Cover, Inside Left, Inside Right */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Card Images
            </Typography>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3
            }}>
              {/* Cover Image */}
              {renderImageUploadSection('cover', 'Cover', coverImageId, coverFileInputRef)}
              
              {/* Inside Left Image */}
              {renderImageUploadSection('inside1', 'Inside Left', inside1ImageId, inside1FileInputRef)}
              
              {/* Inside Right Image */}
              {renderImageUploadSection('inside2', 'Inside Right', inside2ImageId, inside2FileInputRef)}
            </Box>
          </CardContent>
        </Card>

        {/* Delete Card Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Card</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the card "{card?.title}"? This action cannot be undone and will permanently remove the card and all its associated data.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCard}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {deleting ? 'Deleting...' : 'Delete Card'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Image Confirmation Dialog */}
        <Dialog
          open={deleteImageDialogOpen}
          onClose={() => {
            setDeleteImageDialogOpen(false);
            setDeletingImageType(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Image</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this {deletingImageType === 'cover' ? 'cover' : deletingImageType === 'inside1' ? 'inside left' : deletingImageType === 'inside2' ? 'inside right' : ''} image? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteImageDialogOpen(false);
                setDeletingImageType(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteImage(deletingImageType)}
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
              disabled={deleting || !deletingImageType}
            >
              {deleting ? 'Deleting...' : 'Delete Image'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default CardDetailPage;
